<template>
    <v-container max-width="1400" class="mx-auto py-8">
        <div class="text-overline font-weight-semibold mb-1">Manage Booking</div>
        <h2 class="text-h5 font-weight-bold mb-6">Your Cruise Booking</h2>

        <v-row align="start" style="--v-gutter-x: 28px; --v-gutter-y: 28px;">
            <!-- Left rail -->
            <v-col cols="12" md="4">
                <PortStopsCard :foundItinerary="itineraryForCard" />
            </v-col>

            <!-- Right: Tabbed content -->
            <v-col cols="12" md="8">
                <v-card elevation="3" rounded="lg">
                    <v-card-title class="d-flex align-center justify-space-between">
                        <div class="text-subtitle-1 font-weight-bold">Booking Tools</div>
                        <div class="text-caption text-medium-emphasis">
                            Booking ID: <strong>{{ booking?.booking_id || '—' }}</strong>
                        </div>
                    </v-card-title>
                    <v-divider />

                    <!-- Main tabs -->
                    <v-tabs v-model="mainTab" align-tabs="start" class="px-4">
                        <v-tab value="recap" prepend-icon="mdi-information-outline">Recap</v-tab>
                        <v-tab value="payments" prepend-icon="mdi-cash-multiple">Payments</v-tab>
                        <v-tab value="messages" prepend-icon="mdi-message-text-outline">Messages</v-tab>
                    </v-tabs>

                    <v-divider />

                    <v-window v-model="mainTab">
                        <!-- ========== RECAP TAB ========== -->
                        <v-window-item value="recap">
                            <v-card-text>
                                <v-row dense>
                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 font-weight-medium mb-2">Sailing & Listing</div>
                                        <v-list density="comfortable" lines="one">
                                            <v-list-item title="Suite Code" :subtitle="listing?.selected_code || '—'" />
                                            <v-list-item title="Room Type" :subtitle="listing?.room_type || '—'" />
                                            <v-list-item title="Cabin" :subtitle="listing?.cabin_number || '—'" />
                                            <v-list-item title="Guests" :subtitle="booking?.guests.meta.adults ?? '—'" />
                                            <v-list-item title="Tips Included"
                                                :subtitle="listing?.tips_included || '—'" />
                                            <v-list-item title="Status" :subtitle="listing?.status || '—'" />
                                            <v-list-item title="Final Payment Date"
                                                :subtitle="formatDate(listing?.final_payment_date)" />
                                        </v-list>
                                    </v-col>

                                    <v-col cols="12" md="6">
                                        <div class="text-subtitle-2 font-weight-medium mb-2">Financials</div>
                                        <v-list density="comfortable" lines="one">
                                            <v-list-item title="Cruise Fare" :subtitle="money(listing?.cruise_fare)" />
                                            <v-list-item title="Taxes" :subtitle="money(listing?.taxes)" />
                                            <v-list-item title="Service Fee" :subtitle="money(listing?.service_fee)" />
                                            <v-list-item title="Total Price" :subtitle="money(totalDue)" />
                                            <v-list-item title="Deposit" :subtitle="money(listing?.deposit)" />
                                        </v-list>
                                    </v-col>
                                </v-row>

                                <v-divider class="my-4" />

                                <div class="text-subtitle-2 font-weight-medium mb-2">Guests (read-only)</div>
                                <v-table density="comfortable" class="rounded-lg elevation-1">
                                    <thead>
                                        <tr>
                                            <th class="text-left">First Name</th>
                                            <th class="text-left">Middle Name</th>
                                            <th class="text-left">Last Name</th>
                                            <th class="text-left">Gender</th>
                                            <th class="text-left">Citzenship</th>
                                            <th class="text-left">DOB</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(g, i) in guestsParsed" :key="i">
                                            <td>{{ g.firstName || '—' }}</td>
                                            <td>{{ g.middleName || '—' }}</td>
                                            <td>{{ g.lastName || '—' }}</td>
                                            <td>{{ g.gender || '—' }}</td>
                                            <td>{{ g.citizenship || '—' }}</td>
                                            <td>{{ g.dob || '—' }}</td>
                                        </tr>
                                        <tr v-if="!guestsParsed.length">
                                            <td colspan="4" class="text-medium-emphasis">No guest details on file.</td>
                                        </tr>
                                    </tbody>
                                </v-table>
                            </v-card-text>
                        </v-window-item>

                        <!-- ========== PAYMENTS TAB ========== -->
                        <v-window-item value="payments">
                            <v-card-text>
                                <v-tabs v-model="payTab" align-tabs="start" class="mb-2">
                                    <v-tab value="summary" prepend-icon="mdi-view-dashboard-outline">Summary</v-tab>
                                    <v-tab value="history" prepend-icon="mdi-history">History</v-tab>
                                    <v-tab value="make" prepend-icon="mdi-credit-card-outline">Make Payment</v-tab>
                                </v-tabs>

                                <v-window v-model="payTab">
                                    <v-window-item value="summary" class="my-6">
                                        <v-row>
                                            <v-col cols="12" sm="6" md="3">
                                                <div class="text-body-2">Total Due</div>
                                                <div class="text-subtitle-1 font-weight-semibold">{{ money(totalDue) }}
                                                </div>
                                            </v-col>
                                            <v-col cols="12" sm="6" md="3">
                                                <div class="text-body-2">Total Paid</div>
                                                <div class="text-subtitle-1 font-weight-semibold text-success">{{
                                                    money(totalPaid) }}</div>
                                            </v-col>
                                            <v-col cols="12" sm="6" md="3">
                                                <div class="text-body-2">Balance</div>
                                                <div class="text-subtitle-1 font-weight-semibold"
                                                    :class="balance <= 0 ? 'text-success' : ''">
                                                    {{ money(balance) }}
                                                </div>
                                            </v-col>
                                            <v-col cols="12" sm="6" md="3">
                                                <div class="text-body-2">Deposit</div>
                                                <div class="text-subtitle-1">{{ money(listing?.deposit) }}</div>
                                            </v-col>
                                        </v-row>
                                    </v-window-item>

                                    <v-window-item value="history" class="my-6">
                                        <v-data-table :headers="paymentHeaders" :items="paymentsDerived"
                                            :loading="paymentsLoading" class="rounded-lg elevation-1"
                                            density="comfortable" items-per-page="5">
                                            <!-- amount column -->
                                            <template v-slot:[`item.amount`]="{ item }">
                                                <span>{{ money(item.amount) }}</span>
                                            </template>

                                            <!-- created_at column -->
                                            <template v-slot:[`item.created_at`]="{ item }">
                                                <span>{{ formatDateTime(item.created_at) }}</span>
                                            </template>

                                            <!-- no data -->
                                            <template #no-data>
                                                <div class="py-6 text-medium-emphasis">No payments found yet.</div>
                                            </template>
                                        </v-data-table>
                                    </v-window-item>

                                    <v-window-item value="make" class="my-6">
                                        <v-alert v-if="paymentError" type="error" variant="tonal" border="start"
                                            class="mb-4">
                                            {{ paymentError }}
                                        </v-alert>

                                        <v-row dense>
                                            <v-col cols="12" md="4">
                                                <v-text-field v-model.number="paymentForm.amount" label="Amount"
                                                    type="number" min="1" :max="Math.max(balance, 0)" variant="outlined"
                                                    prepend-inner-icon="mdi-currency-usd" density="comfortable" />
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-select v-model="paymentForm.method" :items="paymentMethods"
                                                    label="Payment Method" variant="outlined" density="comfortable" />
                                            </v-col>
                                            <v-col cols="12" md="4">
                                                <v-text-field v-model="paymentForm.reference"
                                                    label="Reference (optional)" variant="outlined"
                                                    density="comfortable" placeholder="e.g., last 4 / txn id" />
                                            </v-col>
                                        </v-row>

                                        <v-textarea v-model="paymentForm.note" label="Note (optional)" auto-grow
                                            rows="2" variant="outlined" density="comfortable" class="mt-2" />

                                        <div class="d-flex justify-space-between align-center mt-4">
                                            <div class="text-medium-emphasis">
                                                New Balance:
                                                <strong>{{ money(Math.max(balance - (paymentForm.amount || 0), 0))
                                                    }}</strong>
                                            </div>
                                            <v-btn color="primary" size="large" :disabled="!canPay"
                                                :loading="paymentSubmitting" @click="submitPayment">
                                                Pay Now
                                            </v-btn>
                                        </div>
                                    </v-window-item>
                                </v-window>
                            </v-card-text>
                        </v-window-item>

                        <!-- ========== MESSAGES TAB ========== -->
                        <v-window-item value="messages">
                            <v-card-text>
                                <v-tabs v-model="chatTab" align-tabs="start" class="mb-3">
                                    <v-tab value="agent" prepend-icon="mdi-account-tie">Booking Agent</v-tab>
                                    <v-tab value="admin" prepend-icon="mdi-shield-account">Admin Team</v-tab>
                                </v-tabs>

                                <v-window v-model="chatTab">
                                    <v-window-item value="agent" class="my-6">
                                        <ChatRoom :room-id="agentRoomId" :title="`Chat with Agent`" :userId="userId" :otherUserId="otherUserId" :listingID="listing.id"
                                            :context="{ bookingId: booking?.booking_id, listingId: listing?.id }" />
                                    </v-window-item>

                                    <v-window-item value="admin" class="my-6">
                                        <ChatRoom :room-id="adminRoomId" :title="`Chat with Admin`" :userId="userId" :otherUserId="listing?.uid" :listingID="listing.id"
                                            :context="{ bookingId: booking?.booking_id, listingId: listing?.id }" />
                                    </v-window-item>
                                </v-window>
                            </v-card-text>
                        </v-window-item>
                    </v-window>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient';
import PortStopsCard from '@/components/PortStopsCard.vue';
import ChatRoom from '@/components/ChatRoom.vue';

export default {
    name: 'ManageBookingClientTabbed',
    components: { PortStopsCard, ChatRoom },
    data() {
        return {
            loading: true,
            booking: null,
            listing: null,

            // tabs
            mainTab: 'recap',
            payTab: 'summary',
            chatTab: 'agent',

            // payments
            payments: [],
            paymentsLoading: false,
            paymentForm: { amount: null, method: null, reference: '', note: '' },
            paymentMethods: ['Card', 'ACH / Bank', 'Other'],
            paymentSubmitting: false,
            paymentError: '',

            paymentHeaders: [
                { title: 'Date', key: 'created_at', sortable: true },
                { title: 'Type', key: 'type', sortable: false },
                { title: 'Amount', key: 'amount', sortable: true },
                { title: 'Method', key: 'method', sortable: false },
                { title: 'Reference', key: 'reference', sortable: false }
            ],

            otherUserId: 'f9889116-3505-4377-9bb7-597800dadc83'
        };
    },
    computed: {
        // pull userId from store (adjust if you keep it elsewhere)
        userId() {
            return this.$store?.state?.user?.uid || null;
        },
        itineraryForCard() {
            return this.listing?.itinerary || null;
        },
        guestsParsed() {
            const g = this.booking?.guests.list;
            if (!g) return [];
            try {
                return Array.isArray(g) ? g : (typeof g === 'string' ? JSON.parse(g) : []);
            } catch {
                return [];
            }
        },
        totalDueBase() {
            const fare = this.num(this.listing?.cruise_fare);
            const taxes = this.num(this.listing?.taxes);
            const platform = this.num(this.listing?.platform_fee);
            const service = this.num(this.listing?.service_fee);
            const explicit = this.num(this.listing?.total_cost);
            const combined = explicit + platform + service;
            return combined > 0 ? combined : fare + taxes + platform + service;
        },
        totalDue() {
            return this.num(this.listing?.total_guest_price) || this.totalDueBase;
        },
        paymentsDerived() {
            const out = [];
            const dep = this.num(this.listing?.deposit);
            if (dep > 0) {
                out.push({
                    type: 'Deposit',
                    amount: dep,
                    method: '—',
                    reference: '',
                    created_at: this.listing?.created_at || this.booking?.created_at || null
                });
            }
            return [...out, ...this.payments];
        },
        totalPaid() {
            return this.paymentsDerived.reduce((sum, p) => sum + this.num(p.amount), 0);
        },
        balance() {
            return Math.max(this.totalDue - this.totalPaid, 0);
        },
        canPay() {
            return !this.paymentSubmitting && this.paymentForm.method && this.num(this.paymentForm.amount) > 0;
        },
        agentRoomId() {
            return this.booking?.booking_id ? `booking:${this.booking.booking_id}:agent` : null;
        },
        adminRoomId() {
            return this.booking?.booking_id ? `booking:${this.booking.booking_id}:admin` : null;
        }
    },
    created() {
        this.fetchAll(); // use id from route
    },
    watch: {
        '$route.params.id': {
            immediate: false,
            async handler() {
                await this.fetchAll();
            }
        }
    },
    methods: {
        // formatting
        num(v) {
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        },
        money(v) {
            const n = this.num(v);
            return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
        },
        formatDate(d) {
            if (!d) return '—';
            const dt = new Date(d);
            return isNaN(dt) ? '—' : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        },
        formatDateTime(d) {
            if (!d) return '—';
            const dt = new Date(d);
            return isNaN(dt)
                ? '—'
                : dt.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
        },

        // data
        async fetchAll() {
            this.loading = true;
            try {
                const idFromPath = this.$route.params.id; // bookings.id (uuid) from /bookings/:id
                if (!idFromPath) throw new Error('Missing route param :id');

                // Booking by primary key id (uuid)
                const { data: booking, error: bErr } = await supabase
                    .from('bookings')
                    .select('*')
                    .eq('id', idFromPath)
                    .maybeSingle();
                if (bErr) throw bErr;
                this.booking = booking;

                // Listing via FK or booking_id link
                this.listing = null;
                if (booking?.listing_id) {
                    const { data: listing, error: lErr } = await supabase
                        .from('listings')
                        .select('*')
                        .eq('id', booking.listing_id)
                        .maybeSingle();
                    if (lErr) throw lErr;
                    this.listing = listing;
                } else if (booking?.booking_id) {
                    const { data: listing, error: lErr } = await supabase
                        .from('listings')
                        .select('*')
                        .eq('booking_id', booking.booking_id)
                        .maybeSingle();
                    if (!lErr && listing) this.listing = listing;
                }

                await this.fetchPayments();
            } catch (e) {
                console.error('Failed to load booking/listing:', e);
            } finally {
                this.loading = false;
            }
        },

        async fetchPayments() {
            this.paymentsLoading = true;
            try {
                if (!this.booking?.booking_id) {
                    this.payments = [];
                    return;
                }
                const { data, error } = await supabase
                    .from('payments')
                    .select('booking_id, listing_id, user_id, amount, method, reference, type, note, created_at')
                    .eq('booking_id', this.booking.booking_id)
                    .order('created_at', { ascending: false });

                if (error) {
                    if (error.code === '42P01') {
                        console.warn('payments table not found — showing only deposit.');
                        this.payments = [];
                        return;
                    }
                    throw error;
                }
                this.payments = (data || []).map(p => ({ ...p, amount: this.num(p.amount), type: p.type || 'Payment' }));
            } catch (e) {
                console.error('Failed to load payments:', e);
                this.payments = [];
            } finally {
                this.paymentsLoading = false;
            }
        },

        async submitPayment() {
            this.paymentError = '';
            if (!this.canPay) return;

            const amount = this.num(this.paymentForm.amount);
            if (amount <= 0) {
                this.paymentError = 'Please enter a valid amount.';
                return;
            }
            if (amount > this.balance) {
                this.paymentError = 'Amount exceeds remaining balance.';
                return;
            }
            if (!this.paymentForm.method) {
                this.paymentError = 'Please choose a payment method.';
                return;
            }

            this.paymentSubmitting = true;
            try {
                const payload = {
                    booking_id: this.booking?.booking_id || null,
                    listing_id: this.listing?.id || null,
                    user_id: this.userId,
                    amount,
                    method: this.paymentForm.method,
                    reference: this.paymentForm.reference || null,
                    type: 'Payment',
                    note: this.paymentForm.note || null,
                    created_at: new Date().toISOString()
                };
                const { error } = await supabase.from('payments').insert([payload]);
                if (error) {
                    if (error.code === '42P01') {
                        console.warn('payments table not found; adding to local list only.');
                        this.payments.unshift(payload);
                    } else {
                        throw error;
                    }
                }
                this.paymentForm = { amount: null, method: null, reference: '', note: '' };
                await this.fetchPayments();
                this.payTab = 'history';
            } catch (e) {
                console.error('Payment error:', e);
                this.paymentError = 'Unable to process payment right now. Please try again.';
            } finally {
                this.paymentSubmitting = false;
            }
        }
    }
};
</script>

<style scoped>
/* tabs + cards keep the scroll minimal already */
</style>
